import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Globe } from 'lucide-react';
import { SiWechat, SiBilibili } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_ITEMS, ROUTE_PATHS } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(72);
  const location = useLocation();
  const dropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const handleMouseEnter = (label: string) => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimerRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-sidebar/95 shadow-lg'
            : 'bg-sidebar'
        }`}
        style={scrolled ? { backdropFilter: 'blur(12px)' } : {}}
      >
        <div className="border-b border-sidebar-border/50">
          <div className="container mx-auto px-4 h-8 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-sidebar-foreground/60">
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                022-23508985
              </span>
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                info@nankai.edu.cn
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                天津市南开区卫津路94号
              </span>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://www.nankai.edu.cn"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-sidebar-foreground/60 hover:text-chart-1 transition-colors"
              >
                <Globe className="w-3 h-3" />
                English
              </a>
              <SiWechat className="w-3.5 h-3.5 text-sidebar-foreground/60 hover:text-chart-1 cursor-pointer transition-colors" />
              <SiBilibili className="w-3.5 h-3.5 text-sidebar-foreground/60 hover:text-chart-1 cursor-pointer transition-colors" />
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-chart-1 to-chart-3 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <span className="text-white font-bold text-base" style={{ fontFamily: 'Noto Serif SC, serif' }}>南</span>
              </div>
              <div className="flex flex-col">
                <span
                  className="text-sidebar-foreground font-bold text-lg leading-tight tracking-wide"
                  style={{ fontFamily: 'Noto Serif SC, serif' }}
                >
                  南开大学
                </span>
                <span className="text-sidebar-foreground/50 text-xs tracking-widest">NANKAI UNIVERSITY</span>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-1 px-4 py-2 rounded text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'text-chart-1 bg-sidebar-accent'
                          : 'text-sidebar-foreground/80 hover:text-chart-1 hover:bg-sidebar-accent'
                      }`
                    }
                  >
                    {item.label}
                    {item.children && (
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${
                          activeDropdown === item.label ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </NavLink>

                  <AnimatePresence>
                    {activeDropdown === item.label && item.children && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.97 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="absolute top-full left-0 mt-1 w-44 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50"
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.path}
                            className="block px-4 py-2.5 text-sm text-card-foreground hover:bg-accent hover:text-accent-foreground transition-colors border-b border-border last:border-b-0"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
              aria-label="切换菜单"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden bg-sidebar border-t border-sidebar-border"
            >
              <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <div key={item.label}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 rounded text-sm font-medium transition-colors ${
                          isActive
                            ? 'text-chart-1 bg-sidebar-accent'
                            : 'text-sidebar-foreground/80 hover:text-chart-1 hover:bg-sidebar-accent'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                    {item.children && (
                      <div className="ml-4 mt-1 flex flex-col gap-0.5">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.path}
                            className="block px-4 py-2 text-xs text-sidebar-foreground/60 hover:text-chart-1 hover:bg-sidebar-accent rounded transition-colors"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-sidebar text-sidebar-foreground">
        <div className="border-t border-sidebar-border">
          <div className="container mx-auto px-4 py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-chart-1 to-chart-3 flex items-center justify-center">
                    <span className="text-white font-bold text-base" style={{ fontFamily: 'Noto Serif SC, serif' }}>南</span>
                  </div>
                  <div>
                    <div className="font-bold text-lg text-sidebar-foreground" style={{ fontFamily: 'Noto Serif SC, serif' }}>南开大学</div>
                    <div className="text-xs text-sidebar-foreground/40 tracking-widest">NANKAI UNIVERSITY</div>
                  </div>
                </div>
                <p className="text-sm text-sidebar-foreground/60 leading-relaxed mb-4">
                  允公允能，日新月异。南开大学创建于1919年，是中国著名的综合性研究型大学。
                </p>
                <div className="flex items-center gap-3">
                  <a href="#" className="w-8 h-8 rounded-full bg-sidebar-accent flex items-center justify-center hover:bg-chart-1/20 transition-colors group">
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-sidebar-accent flex items-center justify-center hover:bg-chart-1/20 transition-colors group">
                    <SiWechat className="w-4 h-4 text-sidebar-foreground/60 group-hover:text-chart-1" />
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-sidebar-accent flex items-center justify-center hover:bg-chart-1/20 transition-colors group">
                    <SiBilibili className="w-4 h-4 text-sidebar-foreground/60 group-hover:text-chart-1" />
                  </a>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-chart-1 mb-4 text-sm tracking-wider uppercase">快速导航</h3>
                <ul className="space-y-2">
                  {[
                    { label: '学校概况', path: ROUTE_PATHS.ABOUT },
                    { label: '院系设置', path: ROUTE_PATHS.ACADEMICS },
                    { label: '科学研究', path: ROUTE_PATHS.RESEARCH },
                    { label: '招生就业', path: ROUTE_PATHS.ADMISSIONS },
                    { label: '新闻资讯', path: ROUTE_PATHS.NEWS },
                  ].map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.path}
                        className="text-sm text-sidebar-foreground/60 hover:text-chart-1 transition-colors flex items-center gap-1"
                      >
                        <span className="w-1 h-1 rounded-full bg-chart-1/40"></span>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-chart-1 mb-4 text-sm tracking-wider uppercase">服务链接</h3>
                <ul className="space-y-2">
                  {[
                    '图书馆',
                    '教务系统',
                    '邮件系统',
                    '校园卡服务',
                    '心理健康中心',
                    '校友会',
                  ].map((item) => (
                    <li key={item}>
                      <a
                        href="#"
                        className="text-sm text-sidebar-foreground/60 hover:text-chart-1 transition-colors flex items-center gap-1"
                      >
                        <span className="w-1 h-1 rounded-full bg-chart-1/40"></span>
                        {item}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-chart-1 mb-4 text-sm tracking-wider uppercase">联系我们</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-chart-1/60 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-sidebar-foreground/60">
                      天津市南开区卫津路94号（八里台校区）
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-chart-1/60 flex-shrink-0" />
                    <span className="text-sm text-sidebar-foreground/60">022-23508985</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-chart-1/60 flex-shrink-0" />
                    <span className="text-sm text-sidebar-foreground/60">info@nankai.edu.cn</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-chart-1/60 flex-shrink-0" />
                    <a
                      href="https://www.nankai.edu.cn"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-sidebar-foreground/60 hover:text-chart-1 transition-colors"
                    >
                      www.nankai.edu.cn
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-sidebar-border/50">
          <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-xs text-sidebar-foreground/40">
              © 2026 南开大学 版权所有 Nankai University. All Rights Reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-sidebar-foreground/40 hover:text-chart-1 transition-colors">隐私政策</a>
              <a href="#" className="text-xs text-sidebar-foreground/40 hover:text-chart-1 transition-colors">网站地图</a>
              <a href="#" className="text-xs text-sidebar-foreground/40 hover:text-chart-1 transition-colors">无障碍访问</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
